import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  request,
  type NamedRolesResponse,
  type NamedRole,
  type PermissionMatrix,
  type StudyMembersResponse,
} from './client';

export { isEndpointMissing } from './conversation';

/**
 * Named roles and per-study access.
 *
 * Both landed on the API on 23 September 2026 (migrations 031–033), so every
 * shape here is the generated one; docs/api-contract-access.md is the record
 * of the contract. `isEndpointMissing` is still re-exported: the two screens
 * keep their Preview state for a deployment pointed at an API older than
 * these routes, where the collection routes answer 404.
 *
 *   GET    /admins/roles/                            admins:read
 *   POST   /admins/roles/                            admins:manage
 *   PATCH  /admins/roles/{key}                       admins:manage
 *   DELETE /admins/roles/{key}                       admins:manage   409 role_in_use
 *   GET    /admins/studies/{slug}/members            admins:read
 *   PUT    /admins/studies/{slug}/members/{adminId}  admins:manage
 *   DELETE /admins/studies/{slug}/members/{adminId}  admins:manage
 *
 * `GET /admins/` rows carry `studies` (empty for an owner), its `roles[]`
 * includes the named roles with `builtIn`, and `PATCH /admins/{id}/role`
 * accepts a named key.
 */

/**
 * Who may hand out what (20 Oct 2026, QA C-ADM-015/016/017). The API refuses,
 * with a sentence, anyone but an owner who gives a permission they do not
 * hold, makes someone an owner, or touches an owner's account; and nobody
 * changes their own role, ticks or PHI-Match. These mirror that, so a picker
 * greys out what would be refused instead of offering it and showing a 403.
 * Only what a change adds counts: what the person already has is not being
 * given.
 */
type Actor = { role: string; permissions: readonly string[] } | null;

/** Said beside whatever is greyed out for this reason. */
export const ONLY_AN_OWNER = 'only an owner can give it';

/** The permissions in `giving` that `actor` may not hand out — none for an owner. */
export function beyondActor(actor: Actor, giving: readonly string[]): string[] {
  if (actor?.role === 'owner') return [];
  return giving.filter((p) => !actor?.permissions.includes(p));
}

/**
 * Why `actor` cannot give the role `roleKey`, or null when they can. `from`
 * is what the person holds now, when known (a fixed or named role's set);
 * an invite and a Custom holder give the role's whole set. Custom itself is
 * an empty set until its boxes are ticked, so it is always offered and the
 * API judges the ticks it carries.
 */
export function roleRefusal(
  actor: Actor,
  matrix: PermissionMatrix,
  roleKey: string,
  from: readonly string[] | null = null,
): string | null {
  if (actor?.role === 'owner') return null;
  if (roleKey === 'owner') return 'Only an owner can make someone an owner.';
  if (roleKey === 'custom') return null;
  const set = matrix.capabilities.find((c) => c.key === roleKey)?.permissions ?? [];
  const lacking = beyondActor(actor, from ? set.filter((p) => !from.includes(p)) : set)[0];
  if (!lacking) return null;
  const label = matrix.permissions.find((p) => p.key === lacking)?.label ?? lacking;
  return `It includes “${label}”, which you do not have yourself. Only an owner can give it.`;
}

export interface RoleInput {
  label: string;
  blurb?: string;
  permissions: string[];
}

export const useRoles = () =>
  useQuery({
    queryKey: ['roles'],
    queryFn: () => request<NamedRolesResponse>('/admins/roles/'),
  });

/** A role change touches the matrix, every holder's row and the member counts. */
function invalidateRoles(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['roles'] });
  void qc.invalidateQueries({ queryKey: ['permission-matrix'] });
  void qc.invalidateQueries({ queryKey: ['admins'] });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RoleInput) =>
      request<{ role: NamedRole }>('/admins/roles/', { method: 'POST', body }),
    onSuccess: () => invalidateRoles(qc),
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, patch }: { key: string; patch: Partial<RoleInput> }) =>
      request<{ role: NamedRole }>(`/admins/roles/${key}`, { method: 'PATCH', body: patch }),
    onSuccess: () => invalidateRoles(qc),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => request<{ ok: boolean }>(`/admins/roles/${key}`, { method: 'DELETE' }),
    onSuccess: () => invalidateRoles(qc),
  });
}

/** `enabled: false` while the preview is showing, so the sample rows are not fetched over. */
export const useStudyMembers = (slug: string | null, enabled = true) =>
  useQuery({
    queryKey: ['study-members', slug],
    queryFn: () => request<StudyMembersResponse>(`/admins/studies/${slug}/members`),
    enabled: slug !== null && enabled,
  });

function useStudyAccessMutation(method: 'PUT' | 'DELETE') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, adminId }: { slug: string; adminId: string }) =>
      request<{ ok: boolean }>(`/admins/studies/${slug}/members/${adminId}`, { method }),
    onSuccess: (_d, { slug }) => {
      void qc.invalidateQueries({ queryKey: ['study-members', slug] });
      // The Users table counts studies per row from the same grant, and the
      // sidebar's study list is the caller's own scope.
      void qc.invalidateQueries({ queryKey: ['admins'] });
      void qc.invalidateQueries({ queryKey: ['meta'] });
    },
  });
}

export const useGrantStudy = () => useStudyAccessMutation('PUT');
export const useRevokeStudy = () => useStudyAccessMutation('DELETE');
